import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '../../context/OrganizationContext';
import { organizationsAPI } from '../../services/api';
import './AcceptInvitation.css';

export default function AcceptInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, refreshOrganizations } = useOrganization();
  const { t } = useTranslation('organization');

  const [loading, setLoading] = useState(true);
  // Se guarda el CÓDIGO ESTABLE, no el texto: el mensaje del servidor viaja en inglés
  // y cambia sin avisar. La pantalla lo traduce al final, con el idioma del usuario.
  const [errorCode, setErrorCode] = useState(null);
  const [success, setSuccess] = useState(false);
  const [redirectingToLogin, setRedirectingToLogin] = useState(false);
  const [orgName, setOrgName] = useState('');

  useEffect(() => {
    if (!token) {
      setErrorCode('token_ausente');
      setLoading(false);
      return;
    }

    if (!user) {
      // Save token and auto-redirect to login
      console.log('[AcceptInvitation] User not logged in, saving token and redirecting to login');
      localStorage.setItem('pendingInviteToken', token);
      localStorage.setItem('pendingInviteUrl', window.location.pathname);

      setRedirectingToLogin(true);
      setLoading(false);

      // Auto-redirect to login page after showing message
      setTimeout(() => {
        navigate('/?redirect=invitation');
      }, 2000);

      return;
    }

    console.log('[AcceptInvitation] User logged in, proceeding with acceptance');

    acceptInvitation();
  }, [user, token]);

  // Check for pending invitation after login
  useEffect(() => {
    if (user && !token) {
      const pendingToken = localStorage.getItem('pendingInviteToken');
      if (pendingToken) {
        localStorage.removeItem('pendingInviteToken');
        navigate(`/accept-invitation/${pendingToken}`);
      }
    }
  }, [user, token, navigate]);

  const acceptInvitation = async () => {
    try {
      setLoading(true);
      setErrorCode(null);

      console.log('[AcceptInvitation] Attempting to accept invitation with token:', token);
      const data = await organizationsAPI.acceptInvitation(token);
      console.log('[AcceptInvitation] Success:', data);

      // Success
      setSuccess(true);
      setOrgName(data.organization?.name || 'la organización');

      // Refresh organizations list
      if (refreshOrganizations) {
        await refreshOrganizations();
      }

      // Full reload (NOT SPA navigate) so OrganizationContext re-runs loadOrgContext
      // and resolves the freshly-joined org into `context`. A SPA navigate would land
      // on /dashboard with stale emptyContext → needsOnboarding:true → the onboarding
      // gate would bounce to /contratar. selectedOrganizationId is consumed by
      // loadOrgContext on the reload.
      setTimeout(() => {
        localStorage.setItem('selectedOrganizationId', data.organization.id);
        window.location.href = '/dashboard';
      }, 2000);

    } catch (err) {
      console.error('[AcceptInvitation] Error:', err);
      console.error('[AcceptInvitation] Error details:', {
        message: err.message,
        response: err.response,
        stack: err.stack
      });

      // EL CÓDIGO ESTABLE, NO EL MENSAJE. Antes se pintaba `err.message` tal cual —el
      // texto del servidor, en inglés— y se le añadía ` (Token: …)` «for debugging».
      // Las dos cosas se retiran:
      //
      //  · el token es la credencial del enlace. Enseñar sus primeros 10 caracteres en
      //    pantalla no sirve para depurar nada que no esté ya en la URL, y sí acaba en
      //    capturas de pantalla que la gente reenvía para pedir ayuda.
      //  · el mensaje crudo decía «Invalid or expired invitation» sobre invitaciones que
      //    no eran ni lo uno ni lo otro. Un texto del servidor no es una explicación
      //    para el usuario: es un dato para quien lee los logs.
      //
      // Si el servidor no manda código —un 500, o un endpoint viejo— cae en `generico`,
      // que no afirma nada que no sepamos.
      setErrorCode(err.code || 'generico');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="accept-invitation-container">
        <div className="accept-invitation-card">
          <div className="spinner-large"></div>
          <h2>Procesando invitación...</h2>
          <p>Por favor espera un momento</p>
        </div>
      </div>
    );
  }

  if (redirectingToLogin) {
    return (
      <div className="accept-invitation-container">
        <div className="accept-invitation-card">
          <div className="spinner-large"></div>
          <h2>Redirigiendo a inicio de sesión</h2>
          <p>Debes iniciar sesión para aceptar esta invitación</p>
          <p className="redirect-message">Redirigiendo en unos segundos...</p>
        </div>
      </div>
    );
  }

  if (errorCode) {
    return (
      <div className="accept-invitation-container">
        <div className="accept-invitation-card error">
          <div className="error-icon">❌</div>
          <h2>{t('aceptarInvitacion.titulo')}</h2>
          {/* `defaultValue` para que un código que el frontend no conozca todavía muestre
              el texto genérico en vez de la clave cruda. Un `organization.aceptarInvitacion.x`
              en pantalla es peor que un mensaje impreciso. */}
          <p>{t(`aceptarInvitacion.${errorCode}`, { defaultValue: t('aceptarInvitacion.generico') })}</p>
          <div className="actions">
            {!user ? (
              <button onClick={() => navigate('/')} className="btn-primary">
                {t('aceptarInvitacion.iniciarSesion')}
              </button>
            ) : (
              <button onClick={() => navigate('/dashboard')} className="btn-primary">
                {t('aceptarInvitacion.volver')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="accept-invitation-container">
        <div className="accept-invitation-card success">
          <div className="success-icon">✅</div>
          <h2>¡Invitación aceptada!</h2>
          <p>Te has unido exitosamente a <strong>{orgName}</strong></p>
          <p className="redirect-message">Redirigiendo a la organización...</p>
        </div>
      </div>
    );
  }

  return null;
}
